import React, { useState } from 'react';
import { X, Pipette, Slash } from 'lucide-react';
import type { TextData } from '../hooks/useStamps';
import { useExclusivePopover } from '../hooks/useExclusivePopover';

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ffffff', '#000000'];
const SIZES = [
  { label: '小', px: 24 },
  { label: '中', px: 36 },
  { label: '大', px: 56 },
] as const;

// 4x supersample，搭配 stamp 縮放仍能保持銳利
const RENDER_SCALE = 4;

export type PopoverPlacement = 'left' | 'right' | 'bottom';

interface TextPopoverProps {
  /** 新增或更新時觸發；caller 自行決定是 addStamp 還是 updateStamp */
  onSubmit: (dataUrl: string, textData: TextData) => void;
  onClose: () => void;
  onRequestEyedropper?: (onPicked: (color: string) => void) => void;
  /** 編輯模式：傳入既有文字資料供 prefill */
  initialValues?: TextData;
  /** popover 展開方向：'left' = 在錨點左邊展開（預設）；'right' = 在右邊 */
  placement?: PopoverPlacement;
}

export const renderTextToDataUrl = (
  text: string,
  fontSize: number,
  color: string,
  backgroundColor?: string,
): string => {
  const renderFontSize = fontSize * RENDER_SCALE;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const font = `bold ${renderFontSize}px sans-serif`;
  ctx.font = font;
  const metrics = ctx.measureText(text);
  // 有底色時 padding 略大，避免文字貼齊邊緣
  const padX = Math.ceil(renderFontSize * (backgroundColor ? 0.4 : 0.2));
  const padY = Math.ceil(renderFontSize * (backgroundColor ? 0.25 : 0.2));
  canvas.width = Math.ceil(metrics.width) + padX * 2;
  canvas.height = renderFontSize + padY * 2;
  // 重設 font（resize canvas 後需重設）
  ctx.font = font;

  // 底色（圓角矩形）
  if (backgroundColor) {
    ctx.fillStyle = backgroundColor;
    const radius = renderFontSize * 0.2;
    ctx.beginPath();
    ctx.roundRect(0, 0, canvas.width, canvas.height, radius);
    ctx.fill();
  }

  // 文字
  ctx.fillStyle = color;
  ctx.textBaseline = 'top';
  ctx.fillText(text, padX, padY);
  return canvas.toDataURL('image/png');
};

interface ColorPaletteRowProps {
  label: string;
  value: string | null;
  onChange: (c: string | null) => void;
  /** 是否顯示「透明」選項（底色用） */
  allowTransparent?: boolean;
  onRequestEyedropper?: (onPicked: (color: string) => void) => void;
}

const ColorPaletteRow: React.FC<ColorPaletteRowProps> = ({
  label, value, onChange, allowTransparent, onRequestEyedropper,
}) => {
  const handlePicked = (c: string) => onChange(c);
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-slate-400 w-9 shrink-0">{label}</span>
      <div className="flex gap-1 items-center flex-wrap">
        {allowTransparent && (
          <button
            type="button"
            title="透明（無底色）"
            onClick={() => onChange(null)}
            className="w-5 h-5 flex items-center justify-center rounded text-rose-400 transition-transform hover:scale-110"
            style={{
              background: 'repeating-conic-gradient(#cbd5e1 0% 25%, #f8fafc 0% 50%) 50% / 8px 8px',
              boxShadow: value === null ? '0 0 0 2px #6366f1' : '0 0 0 1px #cbd5e1',
            }}
          >
            <Slash size={11} strokeWidth={2.5} />
          </button>
        )}
        {COLORS.map(c => (
          <button
            type="button"
            key={c}
            onClick={() => handlePicked(c)}
            className="w-5 h-5 rounded transition-transform hover:scale-110"
            style={{
              background: c,
              boxShadow: value === c
                ? '0 0 0 2px #6366f1'
                : c === '#ffffff' ? '0 0 0 1px #94a3b8' : 'none',
            }}
          />
        ))}
        {onRequestEyedropper && (
          <button
            type="button"
            title="從圖片取色"
            onClick={() => onRequestEyedropper(handlePicked)}
            className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-indigo-500 transition-colors"
          >
            <Pipette size={13} />
          </button>
        )}
      </div>
    </div>
  );
};

const TextPopover: React.FC<TextPopoverProps> = ({ onSubmit, onClose, onRequestEyedropper, initialValues, placement = 'left' }) => {
  useExclusivePopover(onClose);
  const [text, setText] = useState(initialValues?.text ?? '');
  const [fontSize, setFontSize] = useState<number>(initialValues?.fontSize ?? SIZES[1].px);
  const [color, setColor] = useState(initialValues?.color ?? '#ffffff');
  const [bgColor, setBgColor] = useState<string | null>(initialValues?.backgroundColor ?? null);
  const isBottom = placement === 'bottom';
  const placementClass =
    placement === 'left' ? 'absolute right-full mr-2 top-0 w-64' :
    placement === 'right' ? 'absolute left-full ml-2 top-0 w-64' :
    'fixed left-4 right-4 bottom-4 mx-auto max-w-md';

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const dataUrl = renderTextToDataUrl(trimmed, fontSize, color, bgColor ?? undefined);
    onSubmit(dataUrl, {
      text: trimmed, fontSize, color,
      ...(bgColor ? { backgroundColor: bgColor } : {}),
    });
    if (!initialValues) setText('');
    onClose();
  };

  return (
    <>
      {isBottom && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={onClose}
        />
      )}
    <div
      className={`${placementClass} z-50 ${isBottom ? 'animate-in slide-in-from-bottom-4 duration-200 pt-2' : 'max-w-[calc(100vw-2rem)]'} bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl p-3 flex flex-col gap-3`}
      onClick={e => e.stopPropagation()}
    >
      {isBottom && (
        <div className="flex justify-center -mt-1">
          <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
          {initialValues ? '編輯文字' : '加入文字'}
        </span>
        <button className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors" onClick={onClose}>
          <X size={14} />
        </button>
      </div>

      <input
        autoFocus
        type="text"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
        placeholder="輸入文字…"
        className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />

      {/* 大小選擇 — 編輯模式隱藏 */}
      {!initialValues && (
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400 w-9 shrink-0">大小</span>
          <div className="flex gap-1.5">
            {SIZES.map(s => (
              <button
                key={s.label}
                onClick={() => setFontSize(s.px)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all
                  ${fontSize === s.px
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 顏色 — 兩列並列：文字色 / 底色 */}
      <ColorPaletteRow
        label="文字色"
        value={color}
        onChange={c => c && setColor(c)}
        onRequestEyedropper={onRequestEyedropper}
      />
      <ColorPaletteRow
        label="底色"
        value={bgColor}
        onChange={setBgColor}
        allowTransparent
        onRequestEyedropper={onRequestEyedropper}
      />

      {/* 預覽 */}
      <div className="flex items-center justify-center px-2 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 min-h-[36px]">
        {text.trim() ? (
          <span
            className="text-sm font-bold inline-block"
            style={{
              color,
              ...(bgColor ? {
                backgroundColor: bgColor,
                padding: '4px 10px',
                borderRadius: '6px',
              } : {}),
            }}
          >
            {text}
          </span>
        ) : (
          <span className="text-[10px] text-slate-400">預覽</span>
        )}
      </div>

      <button
        disabled={!text.trim()}
        onClick={handleSubmit}
        className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-all"
      >
        {initialValues ? '更新文字' : '加入文字'}
      </button>
    </div>
    </>
  );
};

export default TextPopover;
